import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { EmojiEvents, MilitaryTech } from '../../icons';
import './RankingPanel.css';

const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');

const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Troféu de ouro pro 1º lugar, medalha prata/bronze pro 2º/3º.
const PODIUM_ICONS = {
  1: <EmojiEvents className="ranking-podium-medal-icon ranking-podium-medal-icon--1" />,
  2: <MilitaryTech className="ranking-podium-medal-icon ranking-podium-medal-icon--2" />,
  3: <MilitaryTech className="ranking-podium-medal-icon ranking-podium-medal-icon--3" />
};

// Faixa de cor da % de acerto na lista — mesma leitura de "quente/frio" usada
// nas barras de progresso do resto do dashboard (ver getProgressClass em
// SubjectPerformancePanel), aqui aplicada como badge de percentual.
const getPercentClass = (percentual) => {
  if (percentual >= 80) return 'ranking-percent--high';
  if (percentual >= 50) return 'ranking-percent--medium';
  return 'ranking-percent--low';
};

// Painel "Ranking" (aba própria em MyMocks.js): % de acerto em questões livres,
// por curso ativo. Busca os dados no próprio mount — como o componente só é
// renderizado quando a aba Ranking está ativa, isso já garante que a consulta
// só roda quando o aluno clica na aba (não entra no /dashboard geral).
// Só a linha do próprio usuário mostra o nome completo (studentName, já em
// memória); os demais usam entry.displayName, que já vem anonimizado do
// backend (primeiro nome + iniciais do sobrenome).
const RankingPanel = ({ courseId, studentName }) => {
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!courseId) {
      setRanking(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    axios.get(`${apiUrl}ranking`, { params: { courseId } })
      .then(({ data }) => setRanking(data))
      .catch((err) => {
        console.error('Erro ao buscar ranking:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  const displayNameFor = (entry) => (entry.isYou ? (studentName || 'Você') : entry.displayName);

  const podium = useMemo(() => (ranking?.entries || []).slice(0, 3), [ranking]);
  const podiumOrder = useMemo(
    () => [podium[1], podium[0], podium[2]].filter(Boolean),
    [podium]
  );

  const listRows = useMemo(() => {
    if (!ranking?.entries?.length) return [];
    const rest = ranking.entries.filter((e) => e.rank > 3);
    if (rest.length === 0) return [];

    const top = rest.slice(0, 2);
    const youIndex = rest.findIndex((e) => e.isYou);
    let windowEntries = [];
    if (youIndex !== -1) {
      const start = Math.max(0, youIndex - 2);
      const end = Math.min(rest.length, youIndex + 3);
      windowEntries = rest.slice(start, end);
    }

    const rows = [...top];
    const lastTopRank = top.length ? top[top.length - 1].rank : 3;
    const firstWindowRank = windowEntries.length ? windowEntries[0].rank : null;
    if (firstWindowRank && firstWindowRank > lastTopRank + 1) {
      rows.push({ ellipsis: true, key: 'gap' });
    }
    windowEntries.forEach((entry) => {
      if (!rows.some((r) => r.rank === entry.rank)) rows.push(entry);
    });
    return rows;
  }, [ranking]);

  if (loading) {
    return (
      <div className="dashboard-content-card ranking-panel">
        <div className="empty-state">Carregando ranking...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content-card ranking-panel">
        <div className="empty-state">Não foi possível carregar o ranking agora. Tente novamente mais tarde.</div>
      </div>
    );
  }

  return (
    <div className="ranking-panel">
      <div className="ranking-panel-header">
        <h2>Ranking de questões livres</h2>
        <p>Compare sua % de acerto em questões livres com outros candidatos do seu curso.</p>
      </div>

      {!ranking?.you ? (
        <div className="empty-state ranking-empty-cta">
          Responda pelo menos {ranking?.minQuestoes || 5} questões livres para entrar no ranking.
        </div>
      ) : (
        <>
          {podiumOrder.length > 0 && (
            <div className="ranking-podium">
              {podiumOrder.map((entry) => (
                <div
                  key={entry.rank}
                  className={`ranking-podium-place ranking-podium-place--${entry.rank}${entry.isYou ? ' ranking-podium-place--you' : ''}`}
                >
                  {PODIUM_ICONS[entry.rank]}
                  <div className={`ranking-podium-avatar${entry.isYou ? ' ranking-podium-avatar--you' : ''}`}>
                    {getInitials(displayNameFor(entry))}
                  </div>
                  <span className="ranking-podium-name">
                    {displayNameFor(entry)}{entry.isYou ? ' (Você)' : ''}
                  </span>
                  <span className="ranking-podium-percent">{entry.percentual}%</span>
                  <div className="ranking-podium-bar">
                    <span>{entry.rank}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {listRows.length > 0 && (
            <div className="ranking-list">
              {listRows.map((row) =>
                row.ellipsis ? (
                  <div key={row.key} className="ranking-list-gap">⋯</div>
                ) : (
                  <div
                    key={row.rank}
                    className={`ranking-list-row ranking-list-row--${getPercentClass(row.percentual).replace('ranking-percent--', '')}${row.isYou ? ' ranking-list-row--you' : ''}`}
                  >
                    <span className="ranking-list-rank">{row.rank}</span>
                    <div className="ranking-list-avatar">{getInitials(displayNameFor(row))}</div>
                    <span className="ranking-list-name">
                      {displayNameFor(row)}{row.isYou ? ' (Você)' : ''}
                    </span>
                    <span className="ranking-list-questoes">{row.totalQuestoes} questões</span>
                    <span className={`ranking-list-percent ${getPercentClass(row.percentual)}`}>{row.percentual}%</span>
                  </div>
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RankingPanel;
